/*
 * BKLYN MACS – theme accessibility module (WCAG 2.2 AA remediation, Oct 2026)
 *
 * Theme-owned code. This is NOT an accessibility overlay: it adds no toolbar,
 * no AI repairs and no user-facing widget. It manages state for theme
 * components whose behaviour lives in the vendor-minified theme.min.js
 * (mobile navigation drawer, filter drawer), and contains one clearly-scoped
 * workaround for a defect in the third-party Shopify Forms app embed.
 */
(function () {
  'use strict';

  var FOCUSABLE = 'a[href], area[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), iframe, [tabindex]:not([tabindex="-1"]), [contenteditable="true"]';

  function isVisible(el) {
    if (!el) return false;
    if (el.closest('[inert]')) return false;
    var s = window.getComputedStyle(el);
    if (s.display === 'none' || s.visibility === 'hidden') return false;
    return !!(el.offsetWidth || el.offsetHeight || el.getClientRects().length);
  }

  function focusables(container) {
    return Array.prototype.filter.call(container.querySelectorAll(FOCUSABLE), isVisible);
  }

  function setInert(el, on) {
    if (!el) return;
    if (on) {
      el.setAttribute('inert', '');
      el.setAttribute('aria-hidden', 'true');
    } else {
      el.removeAttribute('inert');
      el.removeAttribute('aria-hidden');
    }
  }

  function trapTab(container, e) {
    if (e.key !== 'Tab') return;
    var items = focusables(container);
    if (!items.length) { e.preventDefault(); return; }
    var first = items[0];
    var last = items[items.length - 1];
    var active = document.activeElement;
    if (e.shiftKey && (active === first || !container.contains(active))) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && (active === last || !container.contains(active))) {
      e.preventDefault();
      first.focus();
    }
  }

  /* ------------------------------------------------------------------ *
   * 1. Mobile navigation drawer (#NavDrawer)
   *    WCAG 1.3.1, 2.1.1, 2.1.2, 2.4.3, 4.1.2
   * ------------------------------------------------------------------ */
  function initNavDrawer() {
    var drawer = document.getElementById('NavDrawer');
    if (!drawer) return;
    var openers = document.querySelectorAll('.js-drawer-open-left-link, [aria-controls="NavDrawer"]');
    var closeBtn = drawer.querySelector('.mobile-menu--close-btn');
    var facets = document.querySelector('collection-filtering-form');
    var lastOpener = null;
    var isOpen = false;

    drawer.setAttribute('role', 'dialog');
    drawer.setAttribute('aria-modal', 'true');
    drawer.setAttribute('aria-label', 'Menu');

    Array.prototype.forEach.call(openers, function (btn) {
      btn.setAttribute('aria-controls', 'NavDrawer');
      btn.setAttribute('aria-expanded', 'false');
      btn.setAttribute('aria-haspopup', 'dialog');
      btn.addEventListener('click', function () { lastOpener = btn; });
    });

    // Sub-menu disclosure buttons: names, state, and focus hand-off between
    // the paired "open"/"close" buttons the theme swaps with CSS.
    Array.prototype.forEach.call(drawer.querySelectorAll('.mobile-nav__has-sublist'), function (row) {
      var link = row.querySelector('.mobile-nav__link');
      var openBtn = row.querySelector('.mobile-nav__toggle-open');
      var closeSub = row.querySelector('.mobile-nav__toggle-close');
      var li = row.closest('li');
      var sublist = li ? li.querySelector(':scope > .mobile-nav__sublist') : null;
      var name = link ? link.textContent.trim() : 'submenu';
      if (li) li.removeAttribute('aria-haspopup');
      if (sublist && !sublist.id) sublist.id = 'MobileNavSub-' + Math.random().toString(36).slice(2, 8);
      [openBtn, closeSub].forEach(function (b) {
        if (!b) return;
        b.removeAttribute('name');
        if (sublist) b.setAttribute('aria-controls', sublist.id);
      });
      if (openBtn) {
        openBtn.setAttribute('aria-label', 'Show ' + name + ' submenu');
        openBtn.setAttribute('aria-expanded', 'false');
        openBtn.addEventListener('click', function () {
          setTimeout(function () { if (isVisible(closeSub)) closeSub.focus(); }, 50);
          syncSublistsSoon();
        });
      }
      if (closeSub) {
        closeSub.setAttribute('aria-label', 'Hide ' + name + ' submenu');
        closeSub.setAttribute('aria-expanded', 'true');
        closeSub.addEventListener('click', function () {
          setTimeout(function () { if (isVisible(openBtn)) openBtn.focus(); }, 50);
          syncSublistsSoon();
        });
      }
    });

    // Collapsed sub-lists must not contain tabbable links.
    function syncSublists() {
      Array.prototype.forEach.call(drawer.querySelectorAll('.mobile-nav__sublist'), function (ul) {
        var rect = ul.getBoundingClientRect();
        var collapsed = window.getComputedStyle(ul).display === 'none' || rect.height < 2;
        if (collapsed) ul.setAttribute('inert', ''); else ul.removeAttribute('inert');
      });
    }

    // Sub-lists animate open/closed; re-check after the transition.
    function syncSublistsSoon() {
      [60, 400, 800].forEach(function (ms) { setTimeout(syncSublists, ms); });
    }

    function restoreTabindex() {
      // theme.min.js only re-enables links below 768px; the hamburger is
      // visible above that width too, so re-enable them whenever open.
      Array.prototype.forEach.call(drawer.querySelectorAll('a[tabindex="-1"], button[tabindex="-1"]'), function (el) {
        el.removeAttribute('tabindex');
      });
    }

    function onKeydown(e) {
      if (!isOpen) return;
      if (e.key === 'Escape' || e.key === 'Esc') {
        if (closeBtn) closeBtn.click();
        return;
      }
      trapTab(drawer, e);
    }

    function open() {
      isOpen = true;
      setInert(drawer, false);
      restoreTabindex();
      syncSublists();
      Array.prototype.forEach.call(openers, function (b) { b.setAttribute('aria-expanded', 'true'); });
      if (!lastOpener) lastOpener = document.activeElement;
      setTimeout(function () {
        var target = closeBtn && isVisible(closeBtn) ? closeBtn : focusables(drawer)[0];
        if (target) target.focus();
      }, 60);
      document.addEventListener('keydown', onKeydown, true);
    }

    function close() {
      isOpen = false;
      setInert(drawer, true);
      Array.prototype.forEach.call(openers, function (b) { b.setAttribute('aria-expanded', 'false'); });
      document.removeEventListener('keydown', onKeydown, true);
      var active = document.activeElement;
      if ((!active || active === document.body || drawer.contains(active)) && lastOpener && isVisible(lastOpener)) {
        lastOpener.focus();
      }
      lastOpener = null;
    }

    function sync() {
      var bodyOpen = document.body.classList.contains('js-drawer-open-left');
      var facetsOpen = facets && facets.classList.contains('is-open');
      var shouldOpen = bodyOpen && !facetsOpen;
      if (shouldOpen && !isOpen) open();
      else if (!shouldOpen && isOpen) close();
      else if (!shouldOpen) setInert(drawer, true);
    }

    new MutationObserver(function () { setTimeout(sync, 0); })
      .observe(document.body, { attributes: true, attributeFilter: ['class'] });
    sync();
  }

  /* ------------------------------------------------------------------ *
   * 2. Collection / search "Filter and sort" drawer
   *    WCAG 2.1.2, 2.4.3, 4.1.2
   * ------------------------------------------------------------------ */
  function initFacetDrawer() {
    var facets = document.querySelector('collection-filtering-form');
    if (!facets) return;
    var openBtn = document.querySelector('[data-drawer-open-btn]');
    var isOpen = false;
    // The theme re-renders the form after filtering, so look the button up each time.
    function getClose() { return facets.querySelector('[data-drawer-close-btn]'); }

    function drawerMode() { return openBtn && isVisible(openBtn); }

    if (openBtn) {
      openBtn.setAttribute('aria-expanded', 'false');
      openBtn.setAttribute('aria-haspopup', 'dialog');
    }

    function onKeydown(e) {
      if (!isOpen) return;
      if (e.key === 'Escape' || e.key === 'Esc') {
        e.preventDefault();
        var c = getClose();
        if (c) c.click();
        return;
      }
      trapTab(facets, e);
    }

    function sync() {
      var open = facets.classList.contains('is-open');
      if (open && !isOpen && drawerMode()) {
        isOpen = true;
        facets.removeAttribute('inert');
        facets.removeAttribute('aria-hidden');
        facets.setAttribute('role', 'dialog');
        facets.setAttribute('aria-modal', 'true');
        facets.setAttribute('aria-label', 'Filter and sort');
        if (openBtn) openBtn.setAttribute('aria-expanded', 'true');
        document.addEventListener('keydown', onKeydown, true);
        setTimeout(function () { var c = getClose(); if (c && isVisible(c)) c.focus(); }, 60);
      } else if (!open && isOpen) {
        isOpen = false;
        facets.removeAttribute('role');
        facets.removeAttribute('aria-modal');
        facets.removeAttribute('aria-label');
        if (openBtn) openBtn.setAttribute('aria-expanded', 'false');
        document.removeEventListener('keydown', onKeydown, true);
        if (drawerMode()) {
          setInert(facets, true);
          var a = document.activeElement;
          if (!a || a === document.body || facets.contains(a)) openBtn.focus();
        }
      } else if (!open) {
        // Closed: hide from keyboard/AT only when it is an off-canvas drawer.
        setInert(facets, drawerMode());
      }
    }

    new MutationObserver(function () { setTimeout(sync, 0); })
      .observe(facets, { attributes: true, attributeFilter: ['class'] });
    var t;
    window.addEventListener('resize', function () { clearTimeout(t); t = setTimeout(sync, 150); });
    sync();
  }

  /* ------------------------------------------------------------------ *
   * 3. Header icon links: remove ARIA that points at non-existent drawers
   *    (Search and Cart navigate to /search and /cart). WCAG 4.1.2
   * ------------------------------------------------------------------ */
  function fixHeaderLinks() {
    Array.prototype.forEach.call(document.querySelectorAll('a[aria-controls]'), function (a) {
      var id = a.getAttribute('aria-controls');
      if (!document.getElementById(id)) {
        a.removeAttribute('aria-controls');
        a.removeAttribute('aria-expanded');
      }
    });
  }

  /* ------------------------------------------------------------------ *
   * 4. THIRD-PARTY WORKAROUND – Shopify Forms app embed
   *    The app renders labels whose `for` points at ids that do not exist
   *    (textarea fields) and an "Event date" text that is not associated
   *    with the MM/DD/YYYY inputs. Reported to Shopify; remove this block
   *    once the app is fixed. WCAG 1.3.1, 3.3.2, 4.1.2
   * ------------------------------------------------------------------ */
  var bmUid = 0;
  function bmId(prefix) { bmUid += 1; return prefix + '-' + bmUid; }
  function addDescribedBy(el, id) {
    var cur = (el.getAttribute('aria-describedby') || '').split(/\s+/).filter(Boolean);
    if (cur.indexOf(id) === -1) { cur.push(id); el.setAttribute('aria-describedby', cur.join(' ')); }
  }
  function removeDescribedBy(el, prefix) {
    var cur = (el.getAttribute('aria-describedby') || '').split(/\s+/).filter(function (x) { return x && x.indexOf(prefix) !== 0; });
    if (cur.length) el.setAttribute('aria-describedby', cur.join(' ')); else el.removeAttribute('aria-describedby');
  }

  function patchShopifyForms(root, host) {
    var wrap = host && host.closest ? host.closest('[data-bm-forms]') : null;

    // a. Labels whose `for` points at an id that does not exist.
    Array.prototype.forEach.call(root.querySelectorAll('label[for]'), function (label) {
      var forId = label.getAttribute('for');
      if (!forId || root.getElementById(forId)) return;
      var container = label.parentElement;
      var control = container && container.querySelector('textarea, input:not([type="hidden"]), select');
      if (!control) return;
      if (control.name === forId && !control.id) {
        control.id = forId; // restores the native label association
      }
      if (label.id && !control.getAttribute('aria-labelledby')) {
        control.setAttribute('aria-labelledby', label.id);
      }
    });

    // b. "Event date" text is not tied to the MM/DD/YYYY inputs.
    Array.prototype.forEach.call(root.querySelectorAll('[data-testid="date-birthday"]'), function (group, i) {
      var heading = group.firstElementChild;
      if (!heading) return;
      if (!heading.id) heading.id = 'bm-date-group-label-' + i;
      group.setAttribute('role', 'group');
      group.setAttribute('aria-labelledby', heading.id);
      Array.prototype.forEach.call(group.querySelectorAll('input'), function (input) {
        input.setAttribute('inputmode', 'numeric');
      });
    });

    // c. An inline (on-page) form is marked up as a modal dialog (role="dialog" aria-modal="true"),
    //    which tells screen readers to ignore the rest of the page. Keep it as a labelled group.
    Array.prototype.forEach.call(root.querySelectorAll('[role="dialog"][aria-modal="true"]'), function (dlg) {
      if (!dlg.closest('[class*="_inline_"]') && !(wrap && wrap.contains(host))) return;
      dlg.removeAttribute('aria-modal');
      dlg.setAttribute('role', 'group');
    });

    var form = root.querySelector('form');
    if (!form) return;

    // d. Autocomplete tokens for personal data (WCAG 1.3.5).
    var ac = { first_name: 'name', email: 'email', phone_number: 'tel-national' };
    Object.keys(ac).forEach(function (id) {
      var el = root.getElementById(id);
      if (el && el.getAttribute('autocomplete') !== ac[id]) {
        el.setAttribute('autocomplete', id === 'phone_number' ? 'tel' : ac[id]);
        if (id === 'email') el.setAttribute('inputmode', 'email');
      }
    });

    // e. Required state, when the page says every field except the upload is required (WCAG 3.3.2, 4.1.2).
    if (wrap && wrap.getAttribute('data-bm-required') === 'all-but-upload') {
      Array.prototype.forEach.call(form.querySelectorAll('input:not([type="hidden"]):not([type="file"]):not([type="radio"]):not([type="checkbox"]), textarea'), function (el) {
        if (/captcha/i.test(el.name || el.id || '')) return;
        if (el.getAttribute('aria-required') !== 'true') el.setAttribute('aria-required', 'true');
      });
      Array.prototype.forEach.call(form.querySelectorAll('fieldset'), function (fs) {
        if (!fs.querySelector('input[type="radio"]')) return;
        fs.setAttribute('role', 'radiogroup');
        fs.setAttribute('aria-required', 'true');
        var legend = fs.querySelector('legend');
        if (legend) { if (!legend.id) legend.id = bmId('bm-legend'); fs.setAttribute('aria-labelledby', legend.id); }
      });
    }

    // f. Upload instructions as real text tied to the file input (was CSS-only).
    var file = form.querySelector('input[type="file"]');
    var hintText = wrap && wrap.getAttribute('data-upload-hint');
    if (file && hintText) {
      var box = file.closest('[class*="_formFileInput_"]') || file.parentElement.parentElement;
      var hint = box.querySelector('.bm-a11y-hint');
      if (!hint) {
        hint = document.createElement('p');
        hint.className = 'bm-a11y-hint';
        hint.id = bmId('bm-upload-hint');
        hint.textContent = hintText;
        box.appendChild(hint);
      }
      addDescribedBy(file, hint.id);
    }

    // g. Error messages: tie each message to its field, mark the field invalid, and replace the
    //    burst of one alert per field with a single summary (WCAG 3.3.1, 4.1.3).
    var outputs = form.querySelectorAll('output');
    Array.prototype.forEach.call(form.querySelectorAll('[aria-describedby]'), function (el) { removeDescribedBy(el, 'bm-err-'); });
    Array.prototype.forEach.call(outputs, function (out) {
      if (!out.id || out.id.indexOf('bm-err-') !== 0) out.id = bmId('bm-err');
      if (out.getAttribute('role') === 'alert') { out.removeAttribute('role'); out.removeAttribute('aria-live'); }
      var targets = [];
      var fs = out.parentElement && out.parentElement.tagName === 'FIELDSET' ? out.parentElement : null;
      if (fs) targets.push(fs);
      else {
        var prev = out.previousElementSibling;
        if (prev) Array.prototype.forEach.call(prev.querySelectorAll('input:not([type="hidden"]), textarea'), function (c) { targets.push(c); });
      }
      targets.forEach(function (t) {
        addDescribedBy(t, out.id);
        if (t.getAttribute('aria-invalid') !== 'true') t.setAttribute('aria-invalid', 'true');
      });
    });
    // Fields without a message are no longer invalid (the app does not reset textareas/radios).
    Array.prototype.forEach.call(form.querySelectorAll('textarea[aria-invalid="true"], fieldset[aria-invalid="true"]'), function (el) {
      if (!(el.getAttribute('aria-describedby') || '').match(/bm-err-/)) el.removeAttribute('aria-invalid');
    });
    // The app flags the date inputs invalid but shows no message for them.
    Array.prototype.forEach.call(form.querySelectorAll('[data-testid="date-birthday"]'), function (group) {
      var invalid = group.querySelector('input[aria-invalid="true"]');
      var msg = group.querySelector('.bm-a11y-error');
      if (invalid && !msg) {
        msg = document.createElement('p');
        msg.className = 'bm-a11y-error';
        msg.id = bmId('bm-err-date');
        msg.textContent = 'Enter the event date as month (MM), day (DD) and year (YYYY).';
        group.appendChild(msg);
      } else if (!invalid && msg) {
        msg.remove();
        msg = null;
      }
      Array.prototype.forEach.call(group.querySelectorAll('input'), function (inp) {
        removeDescribedBy(inp, 'bm-err-date');
        if (msg) addDescribedBy(inp, msg.id);
      });
    });
    // Announce once per submit attempt, not on every keystroke that clears an error.
    if (!form.__bmSubmitHooked) {
      form.__bmSubmitHooked = true;
      form.addEventListener('submit', function () { form.__bmSubmittedAt = Date.now(); form.removeAttribute('data-bm-errcount'); }, true);
    }
    var count = form.querySelectorAll('output').length + form.querySelectorAll('.bm-a11y-error').length;
    var key = String(count);
    var justSubmitted = form.__bmSubmittedAt && Date.now() - form.__bmSubmittedAt < 2500;
    if (justSubmitted && form.getAttribute('data-bm-errcount') !== key) {
      form.setAttribute('data-bm-errcount', key);
      var live = root.querySelector('.bm-a11y-errsummary');
      if (!live) {
        live = document.createElement('div');
        live.className = 'bm-a11y-errsummary';
        live.setAttribute('role', 'alert');
        live.style.cssText = 'position:absolute;width:1px;height:1px;overflow:hidden;clip:rect(0 0 0 0);white-space:nowrap';
        form.parentElement.insertBefore(live, form);
      }
      live.textContent = count ? (count === 1 ? '1 field needs attention.' : count + ' fields need attention.') : '';
    }
  }

  function initShopifyForms() {
    var watched = new WeakSet();
    function scan() {
      Array.prototype.forEach.call(document.querySelectorAll('shopify-forms-embed'), function (embed) {
        var outer = embed.shadowRoot;
        if (!outer) return;
        var inners = [outer];
        Array.prototype.forEach.call(outer.querySelectorAll('*'), function (el) { if (el.shadowRoot) inners.push(el.shadowRoot); });
        inners.forEach(function (root) {
          patchShopifyForms(root, embed);
          if (!watched.has(root)) {
            watched.add(root);
            var pending = false;
            new MutationObserver(function () {
              if (pending) return;
              pending = true;
              setTimeout(function () { pending = false; patchShopifyForms(root, embed); scan(); }, 0);
            }).observe(root, { childList: true, subtree: true, attributes: true, attributeFilter: ['aria-invalid'] });
          }
        });
      });
    }
    if (!document.querySelector('shopify-forms-embed')) {
      // The embed may be injected after load.
      var tries = 0;
      var iv = setInterval(function () {
        tries += 1;
        if (document.querySelector('shopify-forms-embed')) { clearInterval(iv); waitForShadow(); }
        if (tries > 40) clearInterval(iv);
      }, 250);
    } else {
      waitForShadow();
    }
    function waitForShadow() {
      var n = 0;
      var iv2 = setInterval(function () {
        n += 1;
        scan();
        if (n > 120) clearInterval(iv2);
      }, 250);
    }
  }


  /* ------------------------------------------------------------------ *
   * 5. Product media carousels: links inside slides that are scrolled
   *    out of view must not receive focus (WCAG 2.4.3, 2.4.7, 2.4.11).
   * ------------------------------------------------------------------ */
  function initCarousels() {
    Array.prototype.forEach.call(document.querySelectorAll('.swiper'), function (sw) {
      var pending = false;
      function update() {
        pending = false;
        var box = sw.getBoundingClientRect();
        Array.prototype.forEach.call(sw.querySelectorAll('.swiper-slide'), function (slide) {
          var r = slide.getBoundingClientRect();
          var inView = r.width > 0 && r.right > box.left + 1 && r.left < box.right - 1;
          Array.prototype.forEach.call(slide.querySelectorAll('a[href], button, input, select, textarea'), function (el) {
            if (inView) {
              if (el.getAttribute('data-a11y-tabindex') === 'off') {
                el.removeAttribute('tabindex');
                el.removeAttribute('data-a11y-tabindex');
              }
            } else if (el.getAttribute('tabindex') === null) {
              el.setAttribute('tabindex', '-1');
              el.setAttribute('data-a11y-tabindex', 'off');
            }
          });
        });
      }
      function schedule() { if (!pending) { pending = true; setTimeout(update, 350); } }
      var wrapper = sw.querySelector('.swiper-wrapper') || sw;
      new MutationObserver(schedule).observe(wrapper, { attributes: true, subtree: true, attributeFilter: ['class', 'style'] });
      wrapper.addEventListener('transitionend', schedule);
      window.addEventListener('resize', schedule);
      schedule();
    });
  }


  /* ------------------------------------------------------------------ *
   * 6. Filtering / sorting results: keep focus on the control the
   *    shopper used after the theme re-renders the form, and announce
   *    the updated result count (WCAG 2.4.3, 3.2.2, 4.1.3).
   * ------------------------------------------------------------------ */
  function initFilterUpdates() {
    var filters = document.getElementById('main-collection-filters');
    var grid = document.getElementById('CollectionProductGrid') || document.querySelector('[data-wetheme-section-type="template--search"]');
    if (!filters) return;
    var status = document.createElement('div');
    status.className = 'sr-only';
    status.setAttribute('role', 'status');
    status.setAttribute('aria-live', 'polite');
    status.setAttribute('aria-atomic', 'true');
    document.body.appendChild(status);

    var last = null;
    var pending = null;
    function remember(e) {
      var t = e.target;
      var f = document.getElementById('main-collection-filters') || filters;
      if (!t || !f.contains(t)) return;
      last = { id: t.id, name: t.name, value: t.value };
    }
    document.addEventListener('change', remember, true);
    document.addEventListener('click', function (e) {
      var link = e.target.closest && e.target.closest('.js-facet-remove');
      if (link) last = { id: null, name: null, value: null };
    }, true);

    function restore() {
      pending = null;
      var f = document.getElementById('main-collection-filters') || filters;
      var a = document.activeElement;
      if (last && (!a || a === document.body)) {
        var el = (last.id && document.getElementById(last.id)) ||
          (last.name && f.querySelector('[name="' + last.name.replace(/"/g, '\\"') + '"][value="' + String(last.value).replace(/"/g, '\\"') + '"]'));
        if (el) {
          var details = el.closest('details');
          if (details && !details.open) details.open = true;
          el.focus();
        } else {
          var fallback = f.querySelector('summary, select, button:not([data-drawer-open-btn])');
          if (fallback && isVisible(fallback)) fallback.focus();
        }
      }
      last = null;
      var g = document.getElementById('CollectionProductGrid') || grid;
      if (g) {
        var n = g.querySelectorAll(':scope .grid__item').length;
        status.textContent = '';
        setTimeout(function () {
          status.textContent = n === 0 ? 'No products match the selected filters.' : 'Product list updated: ' + n + (n === 1 ? ' product' : ' products') + ' shown.';
        }, 100);
      }
    }
    function schedule(muts) {
      var relevant = muts.some(function (m) { return m.type === 'childList' && (m.addedNodes.length || m.removedNodes.length); });
      if (!relevant || !last) return;
      clearTimeout(pending);
      pending = setTimeout(restore, 300);
    }
    var root = document.querySelector('main') || document.body;
    new MutationObserver(schedule).observe(root, { childList: true, subtree: true });
  }


  /* ------------------------------------------------------------------ *
   * 7. THIRD-PARTY WORKAROUND – VO Product Options (bcpo) checkbox lists
   *    The app renders the flavour checkboxes without a group, so the
   *    instruction ("choose 6 … or 10 …") is not associated with them.
   *    Report to the app vendor; remove once fixed. WCAG 1.3.1, 3.3.2
   * ------------------------------------------------------------------ */
  function patchProductOptions() {
    Array.prototype.forEach.call(document.querySelectorAll('#bcpo .bcpo-checkboxes, #bcpo .bcpo-radios'), function (wrap) {
      if (wrap.getAttribute('role') === 'group') return;
      var title = wrap.querySelector('.bcpo-title');
      if (!title) return;
      if (!title.id) title.id = 'bcpo-title-' + Math.random().toString(36).slice(2, 8);
      wrap.setAttribute('role', 'group');
      wrap.setAttribute('aria-labelledby', title.id);
    });
  }

  function initProductOptions() {
    var n = 0;
    var iv = setInterval(function () {
      n += 1;
      patchProductOptions();
      if (n > 20) clearInterval(iv);
    }, 500);
    initProductOptionsFallback();
  }

  /* ------------------------------------------------------------------ *
   * 8. VO Product Options did not mount (see snippets/product-options-
   *    resilience.liquid for the cause). Show an accessible message, keep
   *    the shopper from adding an unconfigured box, and offer the phone /
   *    email route. WCAG 3.3.1, 4.1.3, 3.3.4-style error prevention.
   * ------------------------------------------------------------------ */
  function initProductOptionsFallback() {
    var form = document.querySelector('form[action*="/cart/add"] button[name="add"]');
    form = form && form.closest('form');
    if (!form) return;
    var TIMEOUT = 8000;
    var started = Date.now();
    function appHasOptions() {
      var d = window.bcpo_data;
      return !!(d && ((d.virtual_options && d.virtual_options.length) || (d.shopify_options && d.shopify_options.length)));
    }
    function mounted() { return !!document.getElementById('bcpo'); }
    var box = null;
    function fail() {
      if (box) return;
      var btn = form.querySelector('button[name="add"]');
      box = document.createElement('div');
      box.className = 'bm-options-fallback';
      box.id = 'bm-options-fallback';
      box.setAttribute('role', 'alert');
      box.innerHTML = '<p><strong>The flavor and message options didn’t load.</strong></p>' +
        '<p>Please reload the page to choose your flavors. If it still doesn’t load, call <a href="tel:+16466923000">646-692-3000</a> or email <a href="mailto:hello@bklynmacs.com">hello@bklynmacs.com</a> and we’ll take your order.</p>' +
        '<p><button type="button" class="bm-options-retry">Reload this page</button></p>';
      box.querySelector('.bm-options-retry').addEventListener('click', function () { window.location.reload(); });
      if (btn) {
        btn.setAttribute('aria-disabled', 'true');
        btn.setAttribute('aria-describedby', box.id);
        btn.addEventListener('click', function (e) { if (!mounted()) { e.preventDefault(); e.stopImmediatePropagation(); box.focus(); } }, true);
        btn.parentElement.insertBefore(box, btn.parentElement.firstChild);
      } else {
        form.insertBefore(box, form.firstChild);
      }
      box.setAttribute('tabindex', '-1');
    }
    function recover() {
      if (!box) return;
      var btn = form.querySelector('button[name="add"]');
      if (btn) { btn.removeAttribute('aria-disabled'); if (btn.getAttribute('aria-describedby') === box.id) btn.removeAttribute('aria-describedby'); }
      box.remove(); box = null;
    }
    // The app boots on requestAnimationFrame and gives up after ~60 frames. Browsers pause
    // rAF in background tabs, so a product opened in a new tab (or loaded while the tab is
    // hidden) can arrive with the options permanently missing. The app exposes a public,
    // re-entrant entry point (window.bcpoFunction); call it once when the tab becomes
    // visible and the app has not mounted. It does nothing if the app already ran.
    var restarted = false;
    function restartApp() {
      if (restarted || mounted() || typeof window.bcpoFunction !== 'function') return false;
      if (!(window.bcpo_product || window.product)) return false;
      restarted = true;
      try {
        window.bcpoFunction(window.bcpo_product || window.product, window.bcpo_settings, window.bcpo_data, (window.bcpo && window.bcpo.oldForm) || null, null);
        started = Date.now(); // give the restarted boot its own timeout window
        return true;
      } catch (e) { return false; }
    }
    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState === 'visible' && !mounted()) restartApp();
    });
    var poll = setInterval(function () {
      if (mounted()) { recover(); clearInterval(poll); return; }
      if (Date.now() - started > TIMEOUT) {
        if (document.visibilityState !== 'visible') return; // paused tab: wait for visibility
        if (!restarted && restartApp()) return;           // one retry while visible
        clearInterval(poll);
        // Only treat as a failure when the app has options to show for this product.
        if (appHasOptions()) fail();
      }
    }, 400);
  }

  /* ------------------------------------------------------------------ */
  function init() {
    try { initNavDrawer(); } catch (e) { console.warn('[a11y] nav drawer', e); }
    try { initFacetDrawer(); } catch (e) { console.warn('[a11y] facets', e); }
    try { fixHeaderLinks(); } catch (e) { console.warn('[a11y] header links', e); }
    try { initShopifyForms(); } catch (e) { console.warn('[a11y] shopify forms', e); }
    try { initCarousels(); } catch (e) { console.warn('[a11y] carousels', e); }
    try { initFilterUpdates(); } catch (e) { console.warn('[a11y] filter updates', e); }
    try { initProductOptions(); } catch (e) { console.warn('[a11y] product options', e); }
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
